import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';
import { toRefusal } from '@core/helpers/installation-refusal.helper';
import { zonedInstant } from '@core/helpers/slot-series.helper';
import {
  Binding,
  BindingCandidate,
  MeasurementPoint,
  MeasurementPointDetail,
} from '@core/models/installation.model';
import { RetractBinding } from '@core/models/installation-write.model';
import { InstallationDataSourcesService } from '@core/services/installation-data-sources.service';
import {
  DialogShellComponent,
  FilledButtonComponent,
  SpinnerComponent,
  TextButtonComponent,
  TextFieldComponent,
} from '@custom-components';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { channelFields, channelKey, channelLabel, pointName } from '../../channels';
import { PlacedRefusal, RefusalPlace, placeRefusal } from '../../editor-refusal';
import { ChannelCandidateComponent } from '../channel-candidate/channel-candidate.component';

export interface PointHistoryDialogData {
  installationOid: string;
  pointOid: string;
  // The zone the periods are shown in.
  timeZone: string;
  points: MeasurementPoint[];
  // Retracting needs its own feature and an Active installation.
  canRetract: boolean;
}

export const RETRACTION_REASON_MAX_LENGTH = 250;
const NO_CORRECTION = '';
const DATE_TIME_FORMAT = 'D MMM YYYY HH:mm';

interface PeriodRow {
  binding: Binding;
  from: string;
  to: string | null;
  source: string;
  closeReason: string | null;
  retractedAt: string | null;
}

interface CorrectionRow {
  key: string;
  candidate: BindingCandidate;
  current: boolean;
  sharedWith: string[];
}

// Every period the point ever had, newest first. Closes with the point as written after a
// retraction, or with nothing.
@Component({
  selector: 'xemex-point-history-dialog',
  imports: [
    ChannelCandidateComponent,
    DialogShellComponent,
    FilledButtonComponent,
    FormsModule,
    MatRadioModule,
    SpinnerComponent,
    TextButtonComponent,
    TextFieldComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './point-history-dialog.component.html',
  styleUrl: './point-history-dialog.component.scss',
})
export class PointHistoryDialogComponent {
  readonly data = inject<PointHistoryDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<PointHistoryDialogComponent, MeasurementPointDetail>>(MatDialogRef);
  private readonly service = inject(InstallationDataSourcesService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly name = pointName(this.data.points, this.data.pointOid) ?? '';
  readonly isLoading = signal(true);
  readonly loadFailed = signal(false);
  readonly detail = signal<MeasurementPointDetail | null>(null);

  readonly retractingOid = signal<string | null>(null);
  readonly reason = signal('');
  readonly reasonTouched = signal(false);
  readonly isLoadingCandidates = signal(false);
  readonly candidatesFailed = signal(false);
  readonly candidates = signal<BindingCandidate[]>([]);
  readonly correctionKey = signal(NO_CORRECTION);
  readonly isSaving = signal(false);
  readonly refusal = signal<PlacedRefusal | null>(null);
  private readonly refusedKey = signal<string | null>(null);
  // Read by the opener once the dialog closes: the point or the installation changed underneath.
  readonly stale = computed(() => this.refusal()?.stale ?? false);

  readonly reasonMaxLength = RETRACTION_REASON_MAX_LENGTH;
  readonly noCorrection = NO_CORRECTION;

  readonly periods = computed<PeriodRow[]>(() =>
    (this.detail()?.bindings ?? []).map(binding => ({
      binding,
      from: this.zoned(binding.validFrom),
      to: binding.validTo === null ? null : this.zoned(binding.validTo),
      source: channelLabel(binding.source),
      closeReason: binding.closeReason === null ? null : this.closeReason(binding.closeReason),
      retractedAt: binding.retracted ? this.zoned(binding.retracted.retractedAt) : null,
    })),
  );

  readonly corrections = computed<CorrectionRow[]>(() => {
    const binding = this.detail()?.binding;
    const currentKey = binding ? channelKey(binding.source) : null;
    return this.candidates().map(candidate => ({
      key: channelKey(candidate.source),
      candidate,
      current: channelKey(candidate.source) === currentKey,
      sharedWith: candidate.sharedWithPoints.map(
        shared => pointName(this.data.points, shared.pointOid) ?? shared.role,
      ),
    }));
  });

  readonly reasonInvalid = computed(() => {
    const length = this.reason().trim().length;
    return length === 0 || length > RETRACTION_REASON_MAX_LENGTH;
  });

  constructor() {
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.loadFailed.set(false);
    this.service
      .point(this.data.installationOid, this.data.pointOid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: detail => {
          this.detail.set(detail);
          this.isLoading.set(false);
        },
        error: (error: unknown) => {
          this.isLoading.set(false);
          const refusal = toRefusal(error);
          if (refusal.status === 404) {
            this.refusal.set(placeRefusal(refusal));
          } else {
            this.loadFailed.set(true);
          }
        },
      });
  }

  startRetract(bindingOid: string): void {
    this.retractingOid.set(bindingOid);
    this.reason.set('');
    this.reasonTouched.set(false);
    this.correctionKey.set(NO_CORRECTION);
    this.refusal.set(null);
    if (this.candidates().length === 0) this.loadCandidates();
  }

  stopRetract(): void {
    this.retractingOid.set(null);
    this.refusal.set(null);
  }

  loadCandidates(): void {
    this.isLoadingCandidates.set(true);
    this.candidatesFailed.set(false);
    this.service
      .candidates(this.data.installationOid, this.data.pointOid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: found => {
          this.candidates.set(found.candidates);
          this.isLoadingCandidates.set(false);
        },
        error: () => {
          this.isLoadingCandidates.set(false);
          this.candidatesFailed.set(true);
        },
      });
  }

  messageAt(place: RefusalPlace): string | null {
    const refusal = this.refusal();
    return refusal?.place === place ? refusal.messageKey : null;
  }

  candidateError(key: string): string | null {
    return key === this.refusedKey() ? this.messageAt('candidate') : null;
  }

  retract(): void {
    const bindingOid = this.retractingOid();
    this.reasonTouched.set(true);
    if (bindingOid === null || this.reasonInvalid()) return;
    const correction = this.corrections().find(row => row.key === this.correctionKey());
    const channel = correction ? channelFields(correction.candidate.source) : null;
    const body: RetractBinding = {
      reason: this.reason().trim(),
      ...(channel ? { source_kind: 'device_channel' as const, ...channel } : {}),
    };
    this.isSaving.set(true);
    this.refusal.set(null);
    this.service
      .retract(this.data.installationOid, this.data.pointOid, bindingOid, body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: detail => this.dialogRef.close(detail),
        error: (error: unknown) => {
          this.isSaving.set(false);
          const refusal = placeRefusal(toRefusal(error));
          this.refusal.set(refusal);
          this.refusedKey.set(correction?.key ?? null);
          // The refusal does not name the holder; the candidates read again do.
          if (refusal.place === 'candidates') this.loadCandidates();
        },
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  // A reason this release does not name yet shows as the server sent it.
  private closeReason(reason: string): string {
    const key = `installations.editor.history.closeReasons.${reason}`;
    const translated = this.translate.instant(key);
    return translated === key ? reason : translated;
  }

  private zoned(instant: string): string {
    return zonedInstant(instant, this.data.timeZone, DATE_TIME_FORMAT);
  }
}
